import React, { useMemo, useState } from 'react';

import { ChevronDown, MapPin } from 'lucide-react';

import CountryFlag from '../common/CountryFlag';

/** One row in a place list: the name, and how many courses sit behind it. */
export interface PlaceOption {
  name: string;
  /** Left out where the list is not read off the courses, e.g. every country in the lookup. */
  count?: number;
}

interface PlaceSelectProps {
  /** Used to word the control's accessible name, e.g. "country". */
  label: string;
  /** What the closed field reads when nothing is picked. */
  placeholder: string;
  /** The "no filter" row at the top of the open list. */
  allLabel: string;
  options: PlaceOption[];
  value: string | null;
  onChange: (next: string | null) => void;
  kind: 'country' | 'region';
  disabled?: boolean;
  searchable?: boolean;
}

// The plate on the left of every row, the same 40px square the course search results use. A
// country gets its flag, and "All countries" (a null name) gets the globe. A region gets a pin:
// there is no flag set for provinces.
const PlacePlate: React.FC<{
  kind: 'country' | 'region';
  name: string | null;
}> = ({ kind, name }) => (
  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white/5 ring-1 ring-white/10">
    {kind === 'country' ? (
      <CountryFlag name={name} />
    ) : (
      <MapPin className="h-5 w-5 text-primary/70" />
    )}
  </span>
);

// A place picker built to look like the course search results rather than a native <select>.
// Shared by the filter drawer and the course request drawer, so picking a country reads the same
// on both.
//
// The native control was doing the job but not looking like it belonged: iOS and Windows both
// draw their own list, in their own colours, so a member tapping Country on a charcoal drawer got
// a white system sheet with blue highlights. This is the same row shape the search dropdown uses
// (40px plate, name, a muted second line) so the two read as one product.
//
// It also carries something the native list could not: how many courses are behind each place, so
// a member can see that a country is worth opening before they pick it.
//
// The list expands in place rather than floating over the drawer. The drawer is full height on a
// phone and scrolls on its own, so an inline list has room and cannot be clipped by an edge or
// land off-screen the way the old floating panel did.
const PlaceSelect: React.FC<PlaceSelectProps> = ({
  label,
  placeholder,
  allLabel,
  options,
  value,
  onChange,
  kind,
  disabled = false,
  searchable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const visibleOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      return options;
    }
    return options.filter((option) => option.name.toLowerCase().includes(term));
  }, [options, query]);

  const select = (next: string | null): void => {
    onChange(next);
    setIsOpen(false);
    setQuery('');
  };

  return (
    <div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-label={value != null ? `${label}: ${value}` : placeholder}
        className={`flex h-14 w-full items-center gap-3 rounded-lg border px-3 text-left transition-colors ${
          disabled
            ? 'cursor-not-allowed border-white/10 text-white/30'
            : 'border-primary/25 bg-black/30 text-white hover:border-primary/60'
        }`}
      >
        {!disabled && <PlacePlate kind={kind} name={value} />}
        <span className="min-w-0 flex-1 truncate text-sm">
          {value ?? placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          } ${disabled ? '' : 'text-primary'}`}
        />
      </button>

      {isOpen && !disabled && (
        <div className="mt-2 overflow-hidden rounded-xl border border-primary/20 bg-charcoal/95">
          {searchable && (
            <div className="border-b border-white/10 p-2">
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={`Search ${label}`}
                aria-label={`Search ${label}`}
                className="h-10 w-full rounded-lg border border-white/15 bg-black/30 px-3 text-sm text-white outline-none placeholder:text-white/30 focus:border-primary"
              />
            </div>
          )}

          {/* Capped and scrolled, like the search results. The drawer scrolls too, so without a
              cap a 200-country list would push the whole form off the bottom of it. */}
          <ul className="max-h-72 overflow-y-auto">
            <li>
              <button
                type="button"
                onClick={() => select(null)}
                className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-primary/10 ${
                  value == null ? 'text-primary' : 'text-white/80'
                }`}
              >
                {/* The globe, so "All countries" lines up with the flags under it. */}
                {kind === 'country' && <PlacePlate kind={kind} name={null} />}
                {allLabel}
              </button>
            </li>

            {visibleOptions.map((option) => (
              <li key={option.name} className="border-t border-white/5">
                <button
                  type="button"
                  onClick={() => select(option.name)}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-primary/10"
                >
                  <PlacePlate kind={kind} name={option.name} />
                  <span className="flex min-w-0 flex-col">
                    <span
                      className={`truncate font-medium ${
                        option.name === value ? 'text-primary' : 'text-white'
                      }`}
                    >
                      {option.name}
                    </span>
                    {option.count != null && (
                      <span className="truncate text-xs text-primary/70">
                        {option.count.toLocaleString('en-US')}{' '}
                        {option.count === 1 ? 'course' : 'courses'}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}

            {visibleOptions.length === 0 && (
              <li className="border-t border-white/5 px-3 py-4 text-sm text-white/40">
                Nothing matches that.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PlaceSelect;
